import { axiosClient } from '@/shared/apis/config';
import { ApiResponse, CustomQueryOptions } from '@/shared/types';
import { useQuery } from '@tanstack/react-query';
import { z } from 'zod';

// 상세 목적
export const contentPurposeDetailSchema = z.object({
  code: z.string(),
  name: z.string(),
});

// 결론 유도
export const contentConclusionGuidanceSchema = z.object({
  code: z.string(),
  name: z.string(),
});

// 추가 입력 필드 (contentPurposeSchema에서 사용하므로 먼저 정의)
export const contentAdditionalInputSchema = z.object({
  code: z.string(),
  name: z.string(),
});

// 목적 (상세 목적 + 결론 유도 + 추가 입력 필드 포함)
export const contentPurposeSchema = z.object({
  code: z.string(),
  name: z.string(),
  details: z.array(contentPurposeDetailSchema),
  conclusionGuidances: z.array(contentConclusionGuidanceSchema),
  additionalInputs: z.array(contentAdditionalInputSchema),
});

// 타겟 연령대
export const contentTargetAgeSchema = z.object({
  code: z.string(),
  name: z.string(),
});

// 타겟 성별
export const contentTargetGenderSchema = z.object({
  code: z.string(),
  name: z.string(),
});

// 톤
export const contentScriptToneSchema = z.object({
  code: z.string(),
  name: z.string(),
});

// 말투
export const contentSpeechStyleSchema = z.object({
  code: z.string(),
  name: z.string(),
});

// 언어
export const contentLanguageSchema = z.object({
  code: z.number(),
  name: z.string(),
});

// 프리셋 (최상위)
export const contentPresetSchema = z.object({
  code: z.string(),
  name: z.string(),
  purposes: z.array(contentPurposeSchema),
  targetAges: z.array(contentTargetAgeSchema),
  targetGenders: z.array(contentTargetGenderSchema),
  tones: z.array(contentScriptToneSchema),
  speechStyles: z.array(contentSpeechStyleSchema),
  additionalInputs: z.array(contentAdditionalInputSchema),
  languages: z.array(contentLanguageSchema),
});

export type ContentPurposeDetailType = z.infer<
  typeof contentPurposeDetailSchema
>;
export type ContentConclusionGuidanceType = z.infer<
  typeof contentConclusionGuidanceSchema
>;
export type ContentPurposeType = z.infer<typeof contentPurposeSchema>;
export type ContentTargetAgeType = z.infer<typeof contentTargetAgeSchema>;
export type ContentTargetGenderType = z.infer<typeof contentTargetGenderSchema>;
export type ContentScriptToneType = z.infer<typeof contentScriptToneSchema>;
export type ContentSpeechStyleType = z.infer<typeof contentSpeechStyleSchema>;
export type ContentAdditionalInputType = z.infer<
  typeof contentAdditionalInputSchema
>;
export type ContentLanguageType = z.infer<typeof contentLanguageSchema>;
export type ContentPresetType = z.infer<typeof contentPresetSchema>;

type ResponseType = ApiResponse<ContentPresetType[]>;

export const getContentPresetsApi = async (): Promise<ResponseType> => {
  try {
    const response = await axiosClient.get<ResponseType>(
      '/v1.0/project/content-presets',
    );
    return response.data;
  } catch (error) {
    return Promise.reject(error);
  }
};

export const QueryKeyUseGetContentPresetsApi = 'getContentPresets' as const;

export const useGetContentPresetsApi = (
  queryOptions?: CustomQueryOptions<ResponseType>,
) => {
  const { queryKey, ...rest } = queryOptions ?? {};

  return useQuery({
    queryKey: [QueryKeyUseGetContentPresetsApi, ...(queryKey ?? [])],
    queryFn: () => getContentPresetsApi(),
    ...rest,
  });
};
